import { useEffect, useState } from 'react';
import { detect, fromNavigator } from "@lingui/detect-locale";
import { Wrench, ArrowRight } from 'lucide-react';

type SupportedLang = 'ko' | 'ja' | 'en';

interface MaintenanceContent {
    title: string;
    subtitle: string;
    description: string[];
    thankYou: string;
}

const content: Record<SupportedLang, MaintenanceContent> = {
    ko: {
        title: '시스템 점검 중',
        subtitle: '서비스 점검 안내',
        description: [
            '더 나은 서비스 제공을 위해 시스템 점검을 진행하고 있습니다.',
            '서비스 이용에 불편을 드려 죄송합니다. 잠시 후 다시 시도해 주세요.',
        ],
        thankYou: '감사합니다.',
    },
    ja: {
        title: 'システムメンテナンス中',
        subtitle: 'メンテナンスのお知らせ',
        description: [
            'より良いサービスを提供するため、システムメンテナンスを実施しています。',
            'ご不便をおかけして申し訳ございません。しばらくしてから再度お試しください。',
        ],
        thankYou: 'ありがとうございます。',
    },
    en: {
        title: 'System Maintenance',
        subtitle: 'Service Maintenance Notice',
        description: [
            'We are currently performing system maintenance to improve our service.',
            'We apologize for any inconvenience. Please try again in a little while.',
        ],
        thankYou: 'Thank you for your patience.',
    },
};

const MaintenancePage: React.FC = () => {
    const [lang, setLang] = useState<SupportedLang>('en');

    useEffect(() => {
        const browserLang = detect(fromNavigator(), () => 'en');
        const detected = browserLang?.substring(0, 2) as SupportedLang;
        if (['ko', 'ja', 'en'].includes(detected)) {
            setLang(detected);
        }
    }, []);

    const c = content[lang];

    return (
        <div className="min-h-screen w-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-page-dark-text relative overflow-hidden font-sans selection:bg-amber-500/30">
            {/* Animated Background Effects */}
            <div className="absolute inset-0 z-0 overflow-hidden">
                {/* Gradient Orbs */}
                <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-amber-600/10 blur-[120px] rounded-full animate-pulse"></div>
                <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-blue-600/10 blur-[100px] rounded-full animate-pulse delay-700"></div>
                <div className="absolute top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2 w-[30%] h-[30%] bg-primary/5 blur-[80px] rounded-full"></div>

                {/* Subtle Grid Pattern */}
                <div
                    className="absolute inset-0 opacity-[0.02]"
                    style={{
                        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                        backgroundSize: '60px 60px'
                    }}
                ></div>

                {/* Floating Particles */}
                <div className="absolute h-2 w-2 bg-amber-400/60 rounded-full top-[10%] left-[20%] animate-ping"></div>
                <div className="absolute h-1.5 w-1.5 bg-primary/80 rounded-full top-[30%] right-[15%] animate-ping delay-500"></div>
                <div className="absolute h-1 w-1 bg-yellow-400/40 rounded-full bottom-[20%] left-[30%] animate-ping delay-1000"></div>
            </div>

            {/* Main Content */}
            <div className="z-10 flex flex-col items-center text-center p-6 md:p-8 max-w-2xl w-full">
                {/* Status Badge */}
                <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 backdrop-blur-sm mb-8">
                    <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                    </span>
                    <span className="text-sm font-semibold tracking-wide text-amber-300">MAINTENANCE</span>
                </div>

                {/* Icon */}
                <div className="relative mb-6">
                    <div className="absolute inset-0 bg-amber-500/20 blur-2xl rounded-full"></div>
                    <div className="relative flex items-center justify-center w-24 h-24 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg shadow-amber-500/30">
                        <Wrench className="w-12 h-12 text-page-dark-text animate-pulse" />
                    </div>
                </div>

                {/* Title */}
                <h1 className="text-3xl md:text-4xl font-black mb-3 tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-orange-200 to-yellow-100">
                    {c.title}
                </h1>

                <h2 className="text-lg md:text-xl font-medium mb-6 text-amber-200/80">
                    {c.subtitle}
                </h2>

                {/* Description */}
                <div className="space-y-3 mb-8 max-w-md">
                    {c.description.map((text, idx) => (
                        <p key={idx} className="text-muted-foreground text-base leading-relaxed">
                            {text}
                        </p>
                    ))}
                </div>

                {/* Thank You */}
                <p className="text-muted-foreground text-sm italic">
                    {c.thankYou}
                </p>

                {/* Language Selector */}
                <div className="flex items-center gap-2 mt-8 text-xs text-muted-foreground">
                    {(['ko', 'ja', 'en'] as SupportedLang[]).map((l) => (
                        <button
                            key={l}
                            onClick={() => setLang(l)}
                            className={`px-3 py-1.5 rounded-lg transition-all ${lang === l
                                    ? 'bg-amber-500/20 text-amber-300 font-medium'
                                    : 'hover:bg-card/50 text-muted-foreground hover:text-muted-foreground'
                                }`}
                        >
                            {l === 'ko' ? '한국어' : l === 'ja' ? '日本語' : 'English'}
                        </button>
                    ))}
                </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute bottom-8 left-8 opacity-30 animate-[float_6s_ease-in-out_infinite]">
                <div className="w-16 h-16 rounded-full border-2 border-dashed border-amber-500/30 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full border border-amber-500/20"></div>
                </div>
            </div>

            <div className="absolute top-12 right-12 opacity-20 animate-[float_8s_ease-in-out_infinite_reverse]">
                <ArrowRight className="w-8 h-8 text-blue-400" />
            </div>

            <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
      `}</style>
        </div>
    );
};

export default MaintenancePage;
